import { Alert, Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowBackUp,
  IconCalendar,
  IconDoorExit,
  IconFileInvoice,
  IconFileTypePdf,
  IconHome,
  IconPencil,
  IconStack2,
  IconTag,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { InventoryExitModal } from 'src/Components/Inventory/InventoryExitModal';
import {
  exitReasonLabel,
  formatEuros,
  formatExitDate,
  missingCurrentValueHint,
} from 'src/Components/Inventory/inventoryLabels';
import { KeyValueLine } from 'src/Components/Shared/KeyValueLine';
import { RowActions } from 'src/Components/Shared/RowActions';
import {
  openInventoryPdf,
  useDeleteInventoryItem,
  usePutBackInventoryItem,
} from 'src/Hooks/Mutations/useInventoryMutations';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { inventoryApi } from 'src/Services/API';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { InventoryExitReason, InventoryValueSource } from 'src/Services/API/generated';
import { trackInventoryExported, trackInventoryItemDeleted } from 'src/Services/Mixpanel/events';

import styles from './InventoryItemDetails.module.scss';
import { Props } from './InventoryItemDetails.types';

/** An object of the house, laid out like ExpenseDetails: photos, values (managers), invoices. */
const InventoryItemDetails = ({ item, isManager, rule, onEdit, onDeleted }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { mutate: putBack, isLoading: isPuttingBack } = usePutBackInventoryItem(item.id);
  const { mutate: deleteItem } = useDeleteInventoryItem();
  const [exitOpened, setExitOpened] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isExited = !!item.exitReason;
  const canExportForClaim =
    isManager &&
    isExited &&
    (item.exitReason === InventoryExitReason.LostOrStolen ||
      item.exitReason === InventoryExitReason.Broken);

  const handleDelete = () =>
    deleteItem(item.id, {
      onSuccess: () => {
        trackInventoryItemDeleted({ undone: false });
        onDeleted(item);
      },
      onError: () => {
        notifications.show({
          color: 'red',
          message: t('inventory.item.deleteError', "L'objet n'a pas pu être supprimé."),
        });
      },
    });

  const handlePutBack = () =>
    putBack(undefined, {
      onSuccess: () => {
        notifications.show({
          color: 'teal',
          message: t('inventory.item.putBackDone', 'Remis dans l’inventaire.'),
        });
      },
      onError: () => {
        notifications.show({
          color: 'red',
          message: t('inventory.item.putBackError', "L'objet n'a pas pu être remis."),
        });
      },
    });

  const exportItem = async () => {
    if (!assetId) return;
    setExporting(true);
    try {
      const pdf = await openInventoryPdf(
        async () => (await inventoryApi.inventoryControllerExportItem(assetId, item.id)).data
      );
      trackInventoryExported({
        item_count: 1,
        value_to_complete_count: pdf.valueToCompleteCount,
        scope: 'item',
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('inventory.export.error', "Le PDF n'a pas pu être préparé. Réessayez."),
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.line}>
        <div className={styles.title}>
          {item.quantity > 1 ? `${item.name} (×${item.quantity})` : item.name}
        </div>
        <RowActions
          actions={[
            {
              key: 'edit',
              label: t('common.edit', 'Modifier'),
              icon: <IconPencil size={18} />,
              hidden: !item.canEdit,
              onClick: onEdit,
            },
            {
              key: 'delete',
              label: t('common.delete', 'Supprimer'),
              icon: <IconTrash size={18} />,
              variant: 'danger',
              hidden: !item.canDelete,
              onClick: handleDelete,
              confirm: {
                title: t('inventory.item.deleteTitle', 'Supprimer {{name}} ?', { name: item.name }),
                message: [
                  t(
                    'inventory.item.deleteText',
                    'Supprimer sert à corriger une erreur ou un doublon : la fiche et ses photos disparaissent de l’inventaire. Les factures restent dans Documents, les souvenirs et les actions reliés restent à leur place.'
                  ),
                  isExited
                    ? null
                    : t(
                        'inventory.item.deleteInstead',
                        'Il a été cassé, donné ou vendu ? Sortez-le plutôt de l’inventaire : il restera consultable.'
                      ),
                ]
                  .filter(Boolean)
                  .join(' '),
              },
            },
          ]}
        />
      </div>

      {isExited && (
        <Alert color="orange" className={styles.alert}>
          {t('inventory.item.exitedOn', 'Sorti de l’inventaire le {{date}} · {{reason}}', {
            date: formatExitDate(item.exitDate!),
            reason: exitReasonLabel(t, item.exitReason!),
          })}
          {item.exitNote && <div>{item.exitNote}</div>}
        </Alert>
      )}

      {item.photos.length > 0 && (
        <div className={styles.photos}>
          {item.photos.map(photo => (
            <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
              <img className={styles.photo} src={photo.previewMediumUrl ?? photo.url} alt="" />
            </a>
          ))}
        </div>
      )}

      <div className={styles.properties}>
        <KeyValueLine
          keyIcon={<IconHome className={styles.icon} size={16} />}
          propertyKey={t('inventory.fields.room', 'Pièce')}
          value={item.room?.name ?? '-'}
        />
        <KeyValueLine
          keyIcon={<IconTag className={styles.icon} size={16} />}
          propertyKey={t('inventory.fields.brandAndModel', 'Marque et modèle')}
          value={item.brandAndModel ?? '-'}
        />
        <KeyValueLine
          keyIcon={<IconCalendar className={styles.icon} size={16} />}
          propertyKey={t('inventory.fields.purchaseYear', "Année d'achat")}
          value={item.purchaseYear ? String(item.purchaseYear) : '-'}
        />
        <KeyValueLine
          keyIcon={<IconStack2 className={styles.icon} size={16} />}
          propertyKey={t('inventory.fields.quantity', 'Quantité')}
          value={String(item.quantity)}
        />
      </div>

      {item.condition && (
        <>
          <div className={styles.subtitle}>{t('inventory.fields.condition', 'État')}</div>
          <div className={styles.note}>{item.condition}</div>
        </>
      )}

      {item.notes && (
        <>
          <div className={styles.subtitle}>{t('inventory.fields.notes', 'Notes')}</div>
          <div className={styles.note}>{item.notes}</div>
        </>
      )}

      {item.valuesVisible && (
        <>
          <div className={styles.subtitle}>{t('inventory.fields.valueSection', 'Valeur')}</div>
          <div className={styles.properties}>
            <KeyValueLine
              propertyKey={t('inventory.fields.currentValueEstimated', 'Valeur actuelle estimée')}
              value={
                item.currentValue != null
                  ? formatEuros(item.currentValue)
                  : missingCurrentValueHint(t, item)
              }
            />
            <KeyValueLine
              propertyKey={t('inventory.fields.replacementValue', 'Valeur à neuf')}
              value={
                <>
                  {item.replacementValue != null ? formatEuros(item.replacementValue) : '-'}
                  {item.replacementValueSource === InventoryValueSource.Lucien &&
                    item.replacementValueSourceUrl && (
                      <>
                        {' · '}
                        <a href={item.replacementValueSourceUrl} target="_blank" rel="noreferrer">
                          {t('inventory.item.foundOnline', 'trouvée sur internet')}
                        </a>
                      </>
                    )}
                </>
              }
            />
            <KeyValueLine
              propertyKey={t('inventory.fields.purchasePrice', "Prix d'achat")}
              value={item.purchasePrice != null ? formatEuros(item.purchasePrice) : '-'}
            />
            <KeyValueLine
              propertyKey={t('inventory.item.wear', 'Usure')}
              value={
                item.depreciationEnabled
                  ? t('inventory.settings.rule', 'Usure : {{rate}} % par an, au plus {{max}} %.', {
                      rate: rule.depreciationRatePercent,
                      max: rule.depreciationMaxPercent,
                    })
                  : t('inventory.item.noWear', 'Sans usure')
              }
            />
          </div>
          <Text size="sm" c="dimmed" mt="xs">
            {t(
              'inventory.item.indicative',
              'Estimation indicative. En cas de sinistre, l’expert de l’assurance décide.'
            )}
          </Text>
        </>
      )}

      {item.invoices.length > 0 && (
        <>
          <div className={styles.subtitle}>{t('inventory.invoices.title', 'Factures')}</div>
          <div className={styles.invoices}>
            {item.invoices.map(invoice => (
              <a
                key={invoice.id}
                className={styles.invoice}
                href={invoice.url}
                target="_blank"
                rel="noreferrer"
              >
                <IconFileInvoice size={16} className={styles.icon} />
                <span>{invoice.name}</span>
              </a>
            ))}
          </div>
        </>
      )}

      <Group gap="sm" className={styles.actions}>
        {isExited ? (
          <Button
            variant="outline"
            leftSection={<IconArrowBackUp size={20} />}
            loading={isPuttingBack}
            onClick={handlePutBack}
          >
            {t('inventory.item.putBack', 'Remettre dans l’inventaire')}
          </Button>
        ) : (
          <Button
            variant="outline"
            leftSection={<IconDoorExit size={20} />}
            onClick={() => setExitOpened(true)}
          >
            {t('inventory.item.exit', 'Sortir de l’inventaire')}
          </Button>
        )}
        {canExportForClaim && (
          <Button
            variant="outline"
            leftSection={<IconFileTypePdf size={20} />}
            loading={exporting}
            onClick={exportItem}
          >
            {t('inventory.item.exportForClaim', 'Télécharger la fiche pour la déclaration')}
          </Button>
        )}
      </Group>

      <LinkedEntitiesSection
        entityType={LinkableEntityType.InventoryItem}
        entityId={item.id}
        entityTitle={item.name}
      />

      <InventoryExitModal
        itemId={item.id}
        itemName={item.name}
        isManager={isManager}
        opened={exitOpened}
        onClose={() => setExitOpened(false)}
      />
    </div>
  );
};

export default InventoryItemDetails;
